// Run signature + submit. Mirrors the canonicalization used in
// supabase/functions/submit-run/index.ts. If you change one, change both.

import { CLIENT_VER, getSupabase, isBackendConfigured } from "./client";
import { getSession, getUserId } from "./auth";

const SECRET = (import.meta.env.VITE_SUBMIT_RUN_SECRET as string | undefined) ?? "";

export interface RunReport {
  mapKey: string;
  difficulty: number;     // 1=Squire 2=Knight 3=Lord
  outcome: "win" | "loss";
  wavesCleared: number;
  leaks: number;
  durationMs: number;
  score: number;
  seed: number;
}

export type SubmitResult =
  | { ok: true;  deduped?: boolean; score?: number }
  | { ok: false; reason: string; status?: number };

export function canonical(r: RunReport, userId: string, clientVer: string): string {
  return [
    userId, r.mapKey, r.difficulty, r.outcome,
    r.wavesCleared, r.leaks, r.durationMs, r.score, r.seed, clientVer,
  ].join("|");
}

async function hmacHex(secret: string, msg: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw", new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" }, false, ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(msg));
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Submit a finished run to the backend. Returns a structured result so
 * callers (and the dev console) can see exactly why a run was dropped.
 * Always logs at warn level on failure — silent rejection used to be the
 * single biggest source of "why am I not on the leaderboard?" bugs.
 */
export async function submitRun(report: RunReport): Promise<SubmitResult> {
  if (!isBackendConfigured()) {
    return { ok: false, reason: "backend-not-configured" };
  }
  const sb = getSupabase();
  const session = getSession();
  const uid = getUserId();
  if (!sb)      return logFail({ ok: false, reason: "no-supabase-client" });
  if (!session) return logFail({ ok: false, reason: "not-signed-in" });
  if (!uid)     return logFail({ ok: false, reason: "no-user-id" });
  if (!SECRET)  return logFail({ ok: false, reason: "missing-VITE_SUBMIT_RUN_SECRET" });

  const signature = await hmacHex(SECRET, canonical(report, uid, CLIENT_VER));
  try {
    const { data, error } = await sb.functions.invoke("submit-run", {
      body: { ...report, clientVer: CLIENT_VER, signature },
    });
    if (error) {
      // FunctionsHttpError carries the underlying Response in .context.
      const ctx = (error as { context?: Response }).context;
      const status = ctx?.status;
      let reason = error.message || "edge-function-error";
      try {
        if (ctx && typeof ctx.json === "function") {
          const body = await ctx.json() as { error?: string };
          if (body?.error) reason = body.error;
        }
      } catch { /* best-effort body parse */ }
      return logFail({ ok: false, reason, status });
    }
    const body = (data ?? {}) as { ok?: boolean; deduped?: boolean; score?: number };
    if (body.ok) return { ok: true, deduped: body.deduped, score: body.score };
    return logFail({ ok: false, reason: "edge-function-not-ok" });
  } catch (err) {
    return logFail({
      ok: false,
      reason: err instanceof Error ? err.message : "submit-threw",
    });
  }
}

function logFail(r: SubmitResult & { ok: false }): SubmitResult {
  console.warn("[submit-run] rejected:", r.reason, r.status ? `(status ${r.status})` : "");
  return r;
}
